// src/hooks/useColumnWidths.ts
import { useState, useEffect } from 'react';

/**
 * Хук для сохранения ширины столбцов таблицы в localStorage
 * @param tableId - уникальный идентификатор таблицы
 * @param initialWidths - начальные ширины столбцов
 * @returns {[Record<string, number>, (widths: Record<string, number>) => void]}
 */
export function useColumnWidths(
  tableId: string,
  initialWidths: Record<string, number> = {}
): [Record<string, number>, (widths: Record<string, number>) => void] {
  // Ключ для хранения в localStorage
  const storageKey = `table_column_widths_${tableId}`;

  // Пытаемся получить сохраненные значения из localStorage
  const getInitialState = (): Record<string, number> => {
    try {
      const savedWidths = localStorage.getItem(storageKey);
      return savedWidths ? JSON.parse(savedWidths) : initialWidths;
    } catch (error) {
      console.error('Ошибка при чтении ширины столбцов из localStorage', error);
      return initialWidths;
    }
  };

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(getInitialState);

  // Сохраняем изменения в localStorage
  const updateColumnWidths = (newWidths: Record<string, number>) => {
    setColumnWidths(newWidths);
    try {
      localStorage.setItem(storageKey, JSON.stringify(newWidths));
    } catch (error) {
      console.error('Ошибка при сохранении ширины столбцов в localStorage', error);
    }
  };

  // Применяем начальные значения при изменении tableId
  useEffect(() => {
    setColumnWidths(getInitialState());
  }, [tableId]);

  return [columnWidths, updateColumnWidths];
}